import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { User } from "@dropin/shared/schemas";
import { verificationStateOf } from "@dropin/shared/schemas";
import {
  type AdminUserClaim,
  adminGetUser,
  adminUpdateUser,
} from "../api";
import { CreatorProfileTab } from "../components/CreatorProfileTab";

export function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<AdminUserClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { user, claims } = await adminGetUser(id);
      setUser(user);
      setClaims(claims);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to load");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function decide(verified: boolean) {
    if (!user) return;
    setBusy(true);
    setError(null);
    try {
      await adminUpdateUser(user.id, { verified });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="card text-muted">Loading…</div>;
  if (error && !user) {
    return (
      <div className="card max-w-md">
        <h1 className="text-xl font-semibold tracking-tightish">Couldn't load user</h1>
        <p className="mt-2 text-sm text-rose">{error}</p>
        <button className="btn mt-4" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>
    );
  }
  if (!user) return null;

  const state = verificationStateOf(user);
  const name = user.displayName || user.email;
  const initial = (name?.[0] ?? "?").toUpperCase();
  const location = [user.neighborhood, user.city].filter(Boolean).join(" · ");

  return (
    <div className="grid gap-5 pb-28 md:pb-0">
      {/* Back link */}
      <Link
        to="/dashboard"
        className="text-sm text-muted hover:text-ink inline-flex items-center gap-1 self-start"
      >
        ← Back to verification queue
      </Link>

      {/* Identity header */}
      <section className="card">
        <div className="flex items-start gap-4">
          <div className="shrink-0 h-20 w-20 rounded-full border border-border bg-paper overflow-hidden flex items-center justify-center">
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt=""
                className="h-full w-full object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <span className="text-3xl font-semibold text-muted" aria-hidden>
                {initial}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <StateChip state={state} />
              <span className="chip text-xs">{user.role}</span>
            </div>
            <h1 className="mt-2 text-2xl font-semibold tracking-tightish truncate">{name}</h1>
            {user.displayName && (
              <p className="text-sm text-muted truncate">{user.email}</p>
            )}
            {location && <p className="mt-0.5 text-sm text-muted truncate">{location}</p>}
            <p className="mt-0.5 text-xs text-muted">
              Joined {new Date(user.createdAt).toLocaleDateString()}
              {state === "pending" && user.verificationRequestedAt && (
                <> · Requested verification {relativeTime(user.verificationRequestedAt)}</>
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Full editable profile — same component the creator uses, in admin mode */}
      <CreatorProfileTab user={user} onUpdated={(u) => setUser(u)} mode="admin" />

      {/* Activity */}
      <Section
        title={`Claim activity (${claims.length})`}
        subtitle="Every perk this creator has claimed."
      >
        {claims.length === 0 ? (
          <p className="text-sm text-muted">No claims yet.</p>
        ) : (
          <div className="grid gap-2">
            {claims.map((c) => (
              <ClaimRow key={c.id} claim={c} />
            ))}
          </div>
        )}
      </Section>

      {error && <div className="card text-sm text-rose">{error}</div>}

      {/* Decision bar */}
      <DecisionBar state={state} busy={busy} onDecide={decide} />
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="card">
      <header>
        <h3 className="text-lg font-semibold tracking-tightish">{title}</h3>
        {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
      </header>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function ClaimRow({ claim }: { claim: AdminUserClaim }) {
  const status = claim.status;
  const statusChipCls =
    status === "fulfilled" || status === "approved"
      ? "chip-success"
      : status === "rejected"
        ? "chip"
        : "chip-accent";
  return (
    <div className="rounded-md border border-border bg-paper px-3 py-2">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`${statusChipCls} text-xs`}>{status}</span>
            <span className="text-sm font-semibold">{claim.perkTitle}</span>
          </div>
          <p className="mt-0.5 text-xs text-muted">
            {claim.businessName}
            {claim.businessCity && ` · ${claim.businessCity}`}
            {" · "}
            {relativeTime(claim.createdAt)}
          </p>
        </div>
        {claim.contentUrl && (
          <a
            href={claim.contentUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="text-xs"
          >
            view content ↗
          </a>
        )}
      </div>
    </div>
  );
}

function StateChip({ state }: { state: "unverified" | "pending" | "verified" }) {
  if (state === "verified") return <span className="chip-success text-xs">Verified</span>;
  if (state === "pending") return <span className="chip-accent text-xs">Pending review</span>;
  return <span className="chip text-xs">Unverified</span>;
}

function DecisionBar({
  state,
  busy,
  onDecide,
}: {
  state: "unverified" | "pending" | "verified";
  busy: boolean;
  onDecide: (verified: boolean) => void;
}) {
  return (
    <>
      {/* Sticky on mobile */}
      <div className="md:hidden fixed inset-x-0 bottom-0 z-20 border-t border-border bg-surface/95 backdrop-blur px-4 py-3">
        <Buttons state={state} busy={busy} onDecide={onDecide} size="sm" />
      </div>
      {/* Inline on desktop */}
      <div className="hidden md:flex justify-end">
        <Buttons state={state} busy={busy} onDecide={onDecide} size="md" />
      </div>
    </>
  );
}

function Buttons({
  state,
  busy,
  onDecide,
  size,
}: {
  state: "unverified" | "pending" | "verified";
  busy: boolean;
  onDecide: (verified: boolean) => void;
  size: "sm" | "md";
}) {
  const cls = size === "sm" ? "text-sm" : "";
  if (state === "pending") {
    return (
      <div className="flex gap-2 justify-end">
        <button
          className={`btn-primary ${cls}`}
          onClick={() => onDecide(true)}
          disabled={busy}
          data-testid="admin-detail-approve"
        >
          {busy ? "…" : "Approve"}
        </button>
        <button
          className={`btn ${cls}`}
          onClick={() => onDecide(false)}
          disabled={busy}
          data-testid="admin-detail-reject"
        >
          Reject
        </button>
      </div>
    );
  }
  if (state === "verified") {
    return (
      <div className="flex gap-2 justify-end">
        <button
          className={`btn ${cls}`}
          onClick={() => onDecide(false)}
          disabled={busy}
          data-testid="admin-detail-revoke"
        >
          {busy ? "…" : "Revoke verification"}
        </button>
      </div>
    );
  }
  // unverified
  return (
    <div className="flex gap-2 justify-end">
      <button
        className={`btn ${cls}`}
        onClick={() => onDecide(true)}
        disabled={busy}
        data-testid="admin-detail-force-verify"
      >
        {busy ? "…" : "Verify anyway"}
      </button>
    </div>
  );
}

function relativeTime(iso: string): string {
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diffMin = Math.max(0, Math.floor((now - t) / 60_000));
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const hours = Math.floor(diffMin / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
